import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { scrollToTop } from '../shared/helpers/scrollHelper';
import { canUsePreFilledAddress, isAdding, isEditing } from '../reducers/checkoutShippingReducer';

// components
import CheckoutContinueAction from '../components/shared/actions/CheckoutContinueAction';
import CheckoutCTAButton from '../components/shared/CheckoutCTAButton';
import CheckoutAddShippingAddress from './CheckoutAddShippingAddress';
import CheckoutShippingAddressList from './CheckoutShippingAddressList';

const CheckoutShippingAddressListWrapper = props => {
    const newAddressRef = useRef(null);

    const {
        viewer: viewerRef,
        buyer: buyerRef,
        isAddingShipping,
        isEditingShipping,
        isComputing,
        actions,
        onChange,
        onBlur,
        onSelect,
        onSubmit,
        onComplete,
        shouldScrollToNewAddress,
    } = props;
    const showSubmit = !(isAddingShipping || isEditingShipping);

    const viewer = useFragment(
        graphql`
            fragment CheckoutShippingAddressListWrapper_viewer on Viewer {
                ...CheckoutShippingAddressList_viewer
                ...CheckoutAddShippingAddress_viewer
            }
        `,
        viewerRef
    );
    const buyer = useFragment(
        graphql`
            fragment CheckoutShippingAddressListWrapper_buyer on User {
                ...CheckoutShippingAddressList_buyer
                ...CheckoutAddShippingAddress_buyer
            }
        `,
        buyerRef
    );

    useEffect(() => {
        if (shouldScrollToNewAddress) {
            scrollToTop(newAddressRef.current);
        }
    }, [shouldScrollToNewAddress]);

    return (
        <div data-tn="shipping-address-pane">
            <CheckoutShippingAddressList
                viewer={viewer}
                buyer={buyer}
                actions={actions}
                isComputing={isComputing}
                onSelect={onSelect}
                onChange={onChange}
                onBlur={onBlur}
                onSubmit={onSubmit}
            />
            <div ref={newAddressRef}>
                <CheckoutAddShippingAddress
                    viewer={viewer}
                    buyer={buyer}
                    actions={actions}
                    isSelected={isAddingShipping}
                    isComputing={isComputing}
                    onSelect={onSelect}
                    onChange={onChange}
                    onBlur={onBlur}
                    onSubmit={onSubmit}
                />
            </div>
            {showSubmit && (
                <CheckoutCTAButton
                    label={<CheckoutContinueAction />}
                    marginTop
                    marginBottom
                    dataTn="shipping-continue-button"
                    disabled={isComputing}
                    isSaving={isComputing}
                    onClick={onComplete}
                />
            )}
        </div>
    );
};

CheckoutShippingAddressListWrapper.propTypes = {
    viewer: PropTypes.object.isRequired,
    buyer: PropTypes.object.isRequired,
    shouldScrollToNewAddress: PropTypes.bool.isRequired,
    actions: PropTypes.object.isRequired,
    isComputing: PropTypes.bool.isRequired,
    isAddingShipping: PropTypes.bool.isRequired,
    isEditingShipping: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
};

export default connect(({ shipping }) => ({
    isAddingShipping: isAdding(shipping),
    isEditingShipping: isEditing(shipping),
    shouldScrollToNewAddress:
        canUsePreFilledAddress(shipping) && shipping.shouldScrolledToPreFilledAddress,
}))(CheckoutShippingAddressListWrapper);
